import type { DocMeta, TiptapDoc } from "@klarity/schema";
import { useCallback, useEffect, useRef, useState } from "react";
import type { DraftStore } from "./drafts";

export const AUTOSAVE_DELAY_MS = 5_000;
export const DRAFT_DELAY_MS = 500;
export const SAVE_TIMEOUT_MS = 30_000;
const MAX_RETRY_MS = 60_000;

export type Snapshot = { title: string; meta: DocMeta; content: TiptapDoc };
export type SaveResult = { status: "saved"; version: number } | { status: "conflict" } | { status: "locked" };
export type SaveFn = (docId: string, version: number, snapshot: Snapshot) => Promise<SaveResult>;
export type AutosaveStatus = "clean" | "dirty" | "saving" | "saved" | "offline" | "conflict" | "locked" | "rejected";

/** 4xx (except 408/429) means the server refused this content; repeating the same request will not help. */
function rejected(e: unknown): boolean {
  const status = typeof e === "object" && e !== null && "status" in e ? (e as { status: unknown }).status : undefined;
  return typeof status === "number" && status >= 400 && status < 500 && status !== 408 && status !== 429;
}

/**
 * Per document: callers must remount this hook for each docId (key={docId}); the router already keys document routes.
 */
export function useAutosave(opts: {
  docId: string;
  initialVersion: number;
  save: SaveFn;
  getSnapshot: () => Snapshot;
  drafts: DraftStore;
  enabled: boolean;
  delayMs?: number;
  retryMs?: number;
}) {
  const { docId, initialVersion, enabled, delayMs = AUTOSAVE_DELAY_MS, retryMs = 10_000 } = opts;
  const [status, setStatus] = useState<AutosaveStatus>("clean");
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const live = useRef(opts);
  live.current = opts;
  const version = useRef(initialVersion);
  const dirty = useRef(false);
  const blocked = useRef<"conflict" | "locked" | "rejected" | null>(null);
  const inFlight = useRef<Promise<void> | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryDelay = useRef(retryMs);
  const runRef = useRef<() => Promise<void>>(async () => {});
  // Bumped on unmount / document change: a save that resolves afterwards must not touch state or schedule more saves.
  const epoch = useRef(0);

  const clearTimer = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);
  const schedule = useCallback((ms: number) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      void runRef.current();
    }, ms);
  }, []);

  const writeDraft = useCallback(() => {
    if (draftTimer.current) clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(() => {
      draftTimer.current = null;
      const s = live.current.getSnapshot();
      void live.current.drafts
        .put({ docId, baseVersion: version.current, ...s, savedAt: new Date().toISOString() })
        .catch(() => {});
    }, DRAFT_DELAY_MS);
  }, [docId]);

  const run = useCallback(async (): Promise<void> => {
    if (inFlight.current) {
      await inFlight.current;
      return;
    }
    if (!dirty.current || blocked.current) return;
    clearTimer();
    dirty.current = false;
    setStatus("saving");
    const mine = epoch.current;
    const snapshot = live.current.getSnapshot();
    const attempt = (async () => {
      try {
        let timeout: ReturnType<typeof setTimeout> | undefined;
        const timedOut = new Promise<never>((_, reject) => {
          timeout = setTimeout(() => reject(new Error("The save timed out")), SAVE_TIMEOUT_MS);
        });
        let r: SaveResult;
        try {
          // A request that never settles must not hold the queue; a late result of it is ignored.
          r = await Promise.race([live.current.save(docId, version.current, snapshot), timedOut]);
        } finally {
          clearTimeout(timeout);
        }
        if (epoch.current !== mine) {
          // Unmounted meanwhile: no state updates, but a confirmed save makes the local draft stale.
          if (r.status === "saved" && !dirty.current) await live.current.drafts.delete(docId).catch(() => {});
          return;
        }
        if (r.status === "saved") {
          version.current = r.version;
          retryDelay.current = retryMs;
          setError(null);
          setLastSavedAt(new Date().toISOString());
          if (dirty.current) {
            setStatus("dirty");
            schedule(delayMs);
          } else {
            setStatus("saved");
            // Nothing changed after the snapshot, so a pending draft write would only repeat what was saved.
            if (draftTimer.current) clearTimeout(draftTimer.current);
            draftTimer.current = null;
            await live.current.drafts.delete(docId).catch(() => {});
          }
        } else {
          blocked.current = r.status;
          dirty.current = true;
          setStatus(r.status);
        }
      } catch (e) {
        if (epoch.current !== mine) return;
        dirty.current = true;
        if (rejected(e)) {
          blocked.current = "rejected";
          setError(e instanceof Error ? e.message : "The server refused the change.");
          setStatus("rejected");
          return;
        }
        setStatus("offline");
        schedule(retryDelay.current);
        retryDelay.current = Math.min(retryDelay.current * 2, MAX_RETRY_MS);
      }
    })();
    inFlight.current = attempt;
    try {
      await attempt;
    } finally {
      inFlight.current = null;
    }
  }, [docId, delayMs, retryMs, schedule, clearTimer]);
  runRef.current = run;

  const markDirty = useCallback(() => {
    if (!live.current.enabled) return;
    dirty.current = true;
    writeDraft();
    if (blocked.current === "rejected") blocked.current = null; // a new change deserves a new attempt
    if (blocked.current) return;
    setStatus((s) => (s === "offline" || s === "saving" ? s : "dirty"));
    schedule(delayMs);
  }, [delayMs, schedule, writeDraft]);

  const flush = useCallback(async () => {
    clearTimer();
    if (inFlight.current) await inFlight.current;
    await run();
  }, [run, clearTimer]);

  const setVersion = useCallback((v: number) => {
    version.current = v;
  }, []);
  const unblock = useCallback(() => {
    blocked.current = null;
    if (dirty.current) setStatus("dirty");
  }, []);
  const markClean = useCallback(() => {
    clearTimer();
    if (draftTimer.current) clearTimeout(draftTimer.current);
    draftTimer.current = null;
    dirty.current = false;
    blocked.current = null;
    setError(null);
    setStatus("clean");
  }, [clearTimer]);

  useEffect(
    () => () => {
      epoch.current += 1;
      clearTimer();
      if (draftTimer.current) {
        clearTimeout(draftTimer.current);
        draftTimer.current = null;
        // Leaving within 0.5 s of a change: write the draft now instead of losing it. A save still in flight may
        // yet fail, so it counts as unsaved too.
        if (dirty.current || inFlight.current) {
          try {
            const s = live.current.getSnapshot();
            void live.current.drafts
              .put({ docId, baseVersion: version.current, ...s, savedAt: new Date().toISOString() })
              .catch(() => {});
          } catch {}
        }
      }
    },
    [clearTimer, docId],
  );

  return {
    status: enabled ? status : ("clean" as AutosaveStatus),
    error,
    lastSavedAt,
    version: () => version.current,
    /** Unsaved work: a change not yet sent, or a save still in flight (it may yet fail). */
    pending: () => dirty.current || inFlight.current !== null,
    markDirty,
    flush,
    setVersion,
    unblock,
    markClean,
  };
}
